import type { ReactElement } from "react";
import { ArticleHero } from "../ArticleHero";
import babyHeroImage from "../../assets/images/baby-outdoor-fall-portrait.webp";
import babyStudioImage1 from "../../assets/images/baby-sitter-floral-studio-portrait.webp";
import babyStudioImage2 from "../../assets/images/baby-girl-pink-princess-studio-portrait.webp";
import { ArticlePhoto } from "./ArticlePhoto";
import { UiIcon } from "./components/UiIcons";
import { GuideCtaActions } from "./components/GuideCtaActions";
import "./NewbornPages.scss";
import "./NewbornArticle.scss";
import "./BabyArticle.scss";

const milestones = [
  { age: "3", title: "First expressions", text: "Eye contact, early smiles and beautiful parent-and-baby connection." },
  { age: "6", title: "Sitter stage", text: "Supported or independent sitting, grabbing toes and joyful expressions." },
  { age: "9", title: "Big personality", text: "Crawling, clapping, standing with support and plenty of movement." },
  { age: "12", title: "First birthday", text: "Standing, first steps and a portrait of the whole first year." },
];

export const BestAgeForBabyPhotos = (): ReactElement => (
  <main className="newborn-page newborn-article-page baby-article-page">
    <article>
      <ArticleHero
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "Resources", to: "/resources/" },
          { label: "Baby", to: "/resources/baby/" },
          { label: "What Is the Best Age for Baby Photos?" },
        ]}
        category="Baby milestone guide"
        description="Every stage brings something different—from first smiles and sitting to crawling and first steps."
        imageAlt="Smiling baby photographed during a milestone portrait session"
        imagePosition="center 45%"
        imageSrc={babyHeroImage}
        readingTime="7 min read"
        title="What Is the Best Age for Baby Photos?"
        updatedAt="2026-08-04"
        updatedLabel="Updated August 2026"
      />
      <div className="newborn-shell newborn-article-layout">
        <aside className="newborn-article-callout">
          <span className="newborn-article-callout__icon">
            <UiIcon name="star" />
          </span>
          <div>
            <h2>Quick answer</h2>
            <p>
              Six to nine months is often the most versatile age for baby photos because many babies can sit, interact
              and show plenty of personality. But 3, 6, 9 and 12 months each tell a different and meaningful part of the
              story.
            </p>
          </div>
        </aside>
        <nav aria-label="In this guide" className="newborn-article-toc">
          <h2>In this guide</h2>
          <ol>
            <li>
              <a href="#timeline">Milestone timeline</a>
            </li>
            <li>
              <a href="#three-months">Three months</a>
            </li>
            <li>
              <a href="#sitter">Six-month sitter stage</a>
            </li>
            <li>
              <a href="#nine-months">Nine months</a>
            </li>
            <li>
              <a href="#birthday">First birthday</a>
            </li>
            <li>
              <a href="#choose">How to choose</a>
            </li>
          </ol>
        </nav>
        <section className="newborn-article-section" id="timeline">
          <p className="newborn-article-kicker">A YEAR OF CHANGE</p>
          <h2>Baby Milestone Timeline</h2>
          <p>
            Milestones happen on your baby’s schedule, not on an exact calendar. Choose based on what your baby is doing
            now rather than waiting for a particular date.
          </p>
          <div className="baby-milestone-timeline">
            {milestones.map((item) => (
              <article key={item.age}>
                <strong>{item.age}</strong>
                <small>MONTHS</small>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </section>
        <section className="newborn-article-section newborn-article-split" id="three-months">
          <div>
            <p className="newborn-article-kicker">AROUND THREE MONTHS</p>
            <h2>First smiles and connection</h2>
            <p>
              At three months, photographs often focus on eye contact, early smiles, tiny details and the way your baby
              fits in your arms. Tummy-time portraits may be brief, so the session stays simple and parent-led.
            </p>
            <p>
              This stage is especially meaningful if you missed newborn photos or want to document the transition out of
              the earliest weeks.
            </p>
          </div>
          <ArticlePhoto
            alt="Smiling sitter-age baby in a blue floral studio setup"
            className="newborn-article-split__photo"
            label="PHOTO: 3 MONTHS"
            src={babyStudioImage1}
          />
        </section>
        <section className="newborn-article-section newborn-article-split newborn-article-split--reverse" id="sitter">
          <ArticlePhoto
            alt="Baby girl sitting in a pink princess dress during a milestone session"
            className="newborn-article-split__photo"
            label="PHOTO: SITTER STAGE"
            src={babyStudioImage2}
          />
          <div>
            <p className="newborn-article-kicker">AROUND SIX MONTHS</p>
            <h2>The classic sitter stage</h2>
            <p>
              Once your baby can sit safely—even with a little support—we can create more variety while staying at their
              level. This stage often includes toes, giggles, reaching and wonderful round-cheeked expressions.
            </p>
            <p>
              Book for the milestone, not exactly six months: strong head control and comfortable sitting matter more
              than the calendar.
            </p>
          </div>
        </section>
        <section className="newborn-article-section" id="nine-months">
          <p className="newborn-article-kicker">AROUND NINE MONTHS</p>
          <h2>Movement and big personality</h2>
          <p>
            Nine-month sessions are lively. Your baby may crawl, clap, pull up or move quickly between favorite people.
            We use games and familiar interaction instead of expecting long stretches of stillness.
          </p>
        </section>
        <section className="newborn-article-section" id="birthday">
          <p className="newborn-article-kicker">AROUND ONE YEAR</p>
          <h2>First birthday portraits</h2>
          <p>
            At twelve months, standing, first steps and emerging independence become part of the story. A birthday
            session can be beautifully simple: your baby, family connection, a favorite object and a few understated
            celebratory details.
          </p>
        </section>
        <section className="newborn-article-section" id="choose">
          <p className="newborn-article-kicker">YOUR FAVORITE CHAPTER</p>
          <h2>How to choose the right age</h2>
          <ul className="newborn-article-checklist">
            <li>
              <UiIcon name="check" />
              <span>Choose 3 months for closeness, early smiles and small details.</span>
            </li>
            <li>
              <UiIcon name="check" />
              <span>Choose the sitter stage for maximum portrait variety.</span>
            </li>
            <li>
              <UiIcon name="check" />
              <span>Choose 9 months for movement and expressive play.</span>
            </li>
            <li>
              <UiIcon name="check" />
              <span>Choose 12 months to celebrate the whole first year.</span>
            </li>
          </ul>
        </section>
        <section className="newborn-article-cta baby-article-cta">
          <div>
            <p className="newborn-article-kicker">NO PERFECT AGE REQUIRED</p>
            <h2>Tell me what your baby is doing now</h2>
            <p>Compare baby session options, then contact me if you would like help matching a package to this milestone.</p>
          </div>
          <GuideCtaActions category="baby" />
        </section>
      </div>
    </article>
  </main>
);
export default BestAgeForBabyPhotos;
